import { useEffect, useMemo } from 'react';

import { FormikValues, useFormik } from 'formik';

import {
  adminApproveBooking,
  adminApproveContract,
  adminPharmaciesInfo,
  adminSelfApproveBooking,
  adminSelfApproveContract,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  bookAvailablity,
  ContractTypes,
  getLongTermContractAttributes,
  IAdminApproveDetailsData,
  IAdminRequestDetailsContractModel,
  resetLongTermContractAttributes,
  selfBookAvailablity,
  TravelAllowanceEnums,
} from '@pharmaplan/common';
import { getDaysDistribution, handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';

import { IApproveBooking } from '../../components/Admin/ApproveBooking/ApproveBooking';
import { SelfApproveValidator } from '../../components/Admin/ApproveBooking/validator';
import {
  convertToNullProxy,
  decimalConverterProxy,
  handlerForZeroes,
  parseEmptyStringProxy,
  parseFloatProxy,
} from '../../helpers/proxyHandlers';
import { OtherScreens } from '../../helpers/Constants';
import { useAppDispatch } from '../useAppDispatch';

import useApproveBookingUtils from './useApproveBookingUtils';
import useAdminBookingBlock from '../Admin/useAdminBookingBlock';
import useSyncWorkshiftValues from '../workshift/useSyncWorkshiftValues';
import useSelfService from '../useSelfService';
import { rateLongTermParamsValidation, rateValidation } from '../../helpers/Functions';
import { useAppSelector } from '../useAppSelector';

const {
  accommodationAllowanceType: accommodationAllowanceTypeEnum,
  travelAllowanceType,
  travelAllowanceRatePerKm: travelAllowanceRatePerKmEnum,
  travelAllowanceAmount: travelAllowanceAmountEnum,
  mealAllowance: mealAllowanceEnum,
  accommodationAllowance: accommodationAllowanceEnum,
  emergencyFees: emergencyFeesEnum,
  otherAllowance: otherAllowanceEnum,
} = AllowancesNames;

const { accommodation, meal, travel, emergency, others } = AllowanceEnums;

const useApproveBooking = ({
  workshiftId,
  bookingId,
  fromScreen,
  type,
  eventIds,
  isAutoSyncRecord,
  back,
  professionalId,
  longTermShiftContractId,
}: IApproveBooking) => {
  const dispatch = useAppDispatch();
  const { actionRequiredDialog, disableActions } = useAdminBookingBlock();
  const { isSelfService } = useSelfService();

  const info = useAppSelector(adminPharmaciesInfo);
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { shiftContractAttributes } = longTermAttributes ?? {};

  const { weekdayHours, weekendHours } = useMemo(() =>
    getDaysDistribution(shiftContractAttributes), [shiftContractAttributes]);

  const {
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  } = info ?? {};

  const { details } = useApproveBookingUtils({
    workshiftId,
    eventIds,
    bookingId,
    fromScreen,
    type,
    back,
    professionalId,
    isAutoSyncRecord,
    longTermShiftContractId,
  });

  let approveApis = {
    approveBooking: adminApproveBooking,
    approveContract: adminApproveContract,
    approveAvailability: bookAvailablity,
  };

  if (isSelfService) {
    approveApis = {
      approveContract: adminSelfApproveContract,
      approveBooking: adminSelfApproveBooking,
      approveAvailability: selfBookAvailablity,
    };
  }

  const { approveAvailability, approveBooking, approveContract } = approveApis ?? {};

  const {
    mealAllowance,
    emergencyFees,
    accommodationAllowance,
    pharmacistHourlyRate: detailsPharmacistHourlyRate,
    pharmaId,
    accommodationAllowanceType,
    travelAllowanceTypes,
    travelAllowanceAmount,
    travelAllowanceRatePerKm,
    counterAccommodationAllowance,
    counterAccommodationAllowanceType,
    counterEndDate,
    counterMealAllowance,
    counterStartDate,
    counterTravelAllowanceAmount,
    counterTravelAllowanceRatePerKm,
    counterTravelAllowanceType,
    startDate,
    endDate,
    counterHourlyRate,
    counterEmergencyFees,
    otherAllowance,
    counterOtherAllowance,
    pharmacyHourlyRate: detailsPharmacyHourlyRate,
    travelAllowance,
  } = (details as IAdminApproveDetailsData) ?? {};

  const {
    longTermShiftContract,
    counterShiftContract,
  } = details as IAdminRequestDetailsContractModel;
  const {
    weekDayPaidHourlyRate: counterWeekday,
    weekendPaidHourlyRate: counterWeekend,
    allowanceNotes: counterAllowanceNotes,
  } = counterShiftContract ?? {};
  const { weekDayPaidHourlyRate, weekendPaidHourlyRate, allowanceNotes } = longTermShiftContract ?? {};

  const approveData = {
    travelAllowance,
    mealAllowance: counterMealAllowance || mealAllowance,
    accommodationAllowance:
      counterAccommodationAllowance || accommodationAllowance,
    travelAllowanceAmount:
      counterTravelAllowanceAmount || travelAllowanceAmount,
    travelAllowanceRatePerKm:
      counterTravelAllowanceRatePerKm || travelAllowanceRatePerKm,
    accommodationAllowanceType:
      counterAccommodationAllowanceType || accommodationAllowanceType,
    travelAllowanceTypes: travelAllowanceTypes?.length
      ? travelAllowanceTypes
      : counterTravelAllowanceType,
    pharmacistHourlyRate: counterHourlyRate || detailsPharmacistHourlyRate,
    emergencyFees: counterEmergencyFees || emergencyFees,
    pharmacyHourlyRate: detailsPharmacyHourlyRate,
    otherAllowance: counterOtherAllowance || otherAllowance,
    weekDayPaidHourlyRate: counterWeekday || weekDayPaidHourlyRate,
    weekendPaidHourlyRate: counterWeekend || weekendPaidHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  };

  const approveDates = {
    startDate: counterStartDate ?? startDate,
    endDate: counterEndDate ?? endDate,
  };

  const isBookAvailability = fromScreen === OtherScreens.MatchingAvailabilities
    || fromScreen === OtherScreens.MatchingWorkshifts;

  const onSubmit = (values: FormikValues) => {
    if (!isAutoSyncRecord && disableActions) {
      actionRequiredDialog();
      return;
    }

    const val = {
      travelAllowance: values?.travelAllowance,
      pharmacyHourlyRate: values?.pharmacyHourlyRate,
      pharmacistHourlyRate: values?.pharmacistHourlyRate,
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKmEnum],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmountEnum],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal] || !!isAutoSyncRecord,
        values[mealAllowanceEnum],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceTypeEnum]
          === AllowanceTypesToHandle.paidValuePerNight || !!isAutoSyncRecord,
        values[accommodationAllowanceEnum],
        null,
      ),
      emergencyFees: handleWorkshiftToggles(
        values[emergency] || !!isAutoSyncRecord,
        values[emergencyFeesEnum],
        null,
      ),
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowanceEnum],
        null,
      ),
    };

    const {
      pharmaId: formikPharmaId,
      notesForSalesOrder,
      sendEmailToPharmacist,
      sendEmailToPharmacy,
    } = values ?? {};

    const handledForDecimals = new Proxy(val, parseFloatProxy);
    const handledForNull = new Proxy(handledForDecimals, convertToNullProxy);

    const formikValues = {
      isAutoSyncRecord,
      ...handledForNull,
      bookingId,
      pharmaId: formikPharmaId,
      notesForSalesOrder,
      sendEmailToPharmacist,
      sendEmailToPharmacy,
      sendEmailToProfessional: sendEmailToPharmacist,
      travelAllowanceTypes: values[travelAllowanceType],
      accommodationAllowanceType: handleWorkshiftToggles(
        values[accommodation],
        values[accommodationAllowanceTypeEnum],
        null,
      ),
    };

    if (longTermShiftContractId) {
      const rates = {
        weekDayPaidHourlyRate: values?.weekDayPaidHourlyRate,
        weekendPaidHourlyRate: values?.weekendPaidHourlyRate,
        pharmacyWeekDayHourlyRate: values?.pharmacyWeekDayHourlyRate,
        pharmacyWeekendHourlyRate: values?.pharmacyWeekendHourlyRate,
      };

      const handledForEmptyString = new Proxy(rates, parseEmptyStringProxy);
      const handledForFloat = new Proxy(handledForEmptyString, parseFloatProxy);

      const isPharmacyLongParamsGreater = rateLongTermParamsValidation({
        professionalWeekday: values?.weekDayPaidHourlyRate,
        professionalWeekend: values?.weekendPaidHourlyRate,
        pharmacyWeekday: values?.pharmacyWeekDayHourlyRate,
        pharmacyWeekend: values?.pharmacyWeekendHourlyRate,
        dispatch,
        isSelfService,
        weekdayHours,
        weekendHours,
      });

      if (!isPharmacyLongParamsGreater) {
        return;
      }

      dispatch(
        approveContract({
          longTermContractId: longTermShiftContractId,
          professionalId: professionalId ?? '',
          allowanceNotes: values?.allowanceNotes,
          ...handledForFloat,
        }),
      );

      return;
    }

    const isPharmacyGreaterPharmacist = rateValidation({
      pharmacistRate: handledForDecimals.pharmacistHourlyRate,
      pharmacyRate: handledForDecimals.pharmacyHourlyRate,
      isSelfService,
      dispatch,
    });

    if (!isPharmacyGreaterPharmacist) {
      return;
    }

    if (isBookAvailability) {
      dispatch(
        approveAvailability({
          ...formikValues,
          workshiftId,
          availabilityId: bookingId,
        }),
      );
    } else {
      dispatch(approveBooking({ ...formikValues }));
    }
  };

  const handledForZero = new Proxy(approveData, handlerForZeroes);
  const handledForDecimals = new Proxy(handledForZero, decimalConverterProxy);

  const formik = useFormik({
    initialValues: {
      ...handledForDecimals,
      ...approveDates,
      pharmaId,
      contractType: longTermShiftContractId
        ? ContractTypes.longTerm
        : ContractTypes.shortTerm,
      notesForSalesOrder: '',
      allowanceNotes: counterAllowanceNotes || allowanceNotes,
      sendEmailToPharmacist: false,
      sendEmailToPharmacy: false,
      [accommodationAllowanceTypeEnum]: `${accommodationAllowanceType}`,
      [AllowancesNames.travelAllowanceType]:
        (travelAllowanceTypes?.length
          ? travelAllowanceTypes
          : counterTravelAllowanceType
        )?.filter?.(Boolean) ?? [],
    },
    enableReinitialize: true,
    validationSchema: SelfApproveValidator(),
    onSubmit,
  });

  useSyncWorkshiftValues(formik);

  useEffect(() => {
    formik.setFieldValue(meal, !!mealAllowance);
    formik.setFieldValue(travel, !!travelAllowanceTypes?.length);
    formik.setFieldValue(accommodation, !!accommodationAllowanceType);
    formik.setFieldValue(emergency, !!emergencyFees);
    formik.setFieldValue(others, !!otherAllowance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  useEffect(
    () =>
      () => {
        dispatch(resetLongTermContractAttributes());
      // eslint-disable-next-line react-hooks/exhaustive-deps
      },
    [],
  );
  return { details, formik };
};

export default useApproveBooking;
